class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.mainSlider = this.querySelector(".product-gallery__slider");
    this.thumbSlider = this.querySelector(".product-gallery__thumb__slider");
    this.pauseMediaHandler = this.pauseMedia.bind(this);
    this.gallerySizeHandler = this.updateStickyOffset.bind(this);
    this.pageVisibilityHandler = () => {
      if (document.hidden) this.pauseMedia();
    };
    const productJsonString = document.querySelector("#product-json").innerHTML;
    this.product = JSON.parse(productJsonString);
    this.variantsImages = {};
    this.skuToVariantIds = {}; // Bug 3 fix: SKU -> [variantId, ...] (supports shared SKUs)
    this.genericImages = []; // Bug 2 fix: images not tied to any variant
    this.productImages = this.product.media.filter(
      (m) => m.media_type === "image"
    );

    // variant images — build SKU-to-variant-IDs map and featured images
    this.product.variants.forEach((variant) => {
      if (variant.sku) {
        // Bug 3 fix: map SKU to an array of variant IDs so shared SKUs work
        if (!this.skuToVariantIds[variant.sku]) {
          this.skuToVariantIds[variant.sku] = [];
        }
        this.skuToVariantIds[variant.sku].push(variant.id);
      }
      if(variant.featured_image) {
        if (!this.variantsImages[`${variant.id}`]) {
          this.variantsImages[`${variant.id}`] = [];
        }
        this.variantsImages[`${variant.id}`].push(variant.featured_image);
      }
    });

    // images with same sku in image.alt (comma-separated alt supported)
    this.productImages.forEach((image) => {
      if(image.alt) {
        const image_alts = image.alt.split(',');
        for(let i = 0; i < image_alts.length; i++) {
          const img_alt = image_alts[i].toString().trim();
          if (this.skuToVariantIds.hasOwnProperty(`${img_alt}`)) {
            // Bug 3 fix: add image to ALL variants sharing this SKU
            const variantIds = this.skuToVariantIds[`${img_alt}`];
            variantIds.forEach((variantId) => {
              if (!this.variantsImages[`${variantId}`]) {
                this.variantsImages[`${variantId}`] = [];
              }
              const alreadyExists = this.variantsImages[`${variantId}`].find((img) => img && img.src === image.src);
              if(!alreadyExists) {
                this.variantsImages[`${variantId}`].push(image);
              }
            });
          }
        }
      }
    });

    // Bug 2 fix: identify generic images — not tied to any variant
    const allVariantImageSrcs = new Set();
    Object.values(this.variantsImages).forEach((images) => {
      images.forEach((img) => {
        if (img && img.src) allVariantImageSrcs.add(img.src);
      });
    });
    this.genericImages = this.productImages.filter(
      (img) => !allVariantImageSrcs.has(img.src)
    );

    // Keep Shopify-rendered video/model markup when rebuilding for a variant.
    this.nonImageMarkup = {};
    this.product.media.forEach((media) => {
      if (media.media_type === "image") return;
      const main = this.mainSlider.querySelector(`[data-media-id="${media.id}"]`);
      const thumb = this.thumbSlider.querySelector(`[data-media-id="${media.id}"]`);
      if (main && thumb) {
        this.nonImageMarkup[media.id] = {
          main: main.outerHTML,
          thumb: thumb.outerHTML,
        };
      }
    });
    this.genericMedia = this.product.media.filter((media) =>
      media.media_type === "image"
        ? !allVariantImageSrcs.has(media.src)
        : !!this.nonImageMarkup[media.id]
    );
    const galleryMapElement = this.querySelector('[data-variant-media-map]');
    this.strictVariantGallery = !!galleryMapElement;
    if (galleryMapElement) {
      const galleryMap = JSON.parse(galleryMapElement.textContent);
      const mediaById = new Map(this.productImages.map((media) => [String(media.id), media]));
      this.variantsImages = Object.fromEntries(Object.entries(galleryMap).map(([id, mediaIds]) =>
        [id, mediaIds.map((mediaId) => mediaById.get(String(mediaId))).filter(Boolean)]
      ));
      this.genericImages = [];
      this.genericMedia = [];
    }
    this.init();

    this.addEventListener(
      "variantChanged",
      this.handleVariantChange.bind(this)
    );
  }

  connectedCallback() {
    this.photosColumn = this.closest(".product-photos");
    if (this.photosColumn) {
      this.pageHeader = document.querySelector(".header-site");
      this.headerNavigation = this.pageHeader
        ? Array.from(this.pageHeader.querySelectorAll("[data-sticky-pc], [data-sticky-mb]"))
        : [];
      window.addEventListener("resize", this.gallerySizeHandler);
      if ("ResizeObserver" in window) {
        this.galleryResizeObserver = new ResizeObserver(this.gallerySizeHandler);
        this.galleryResizeObserver.observe(this.photosColumn);
        if (this.pageHeader) this.galleryResizeObserver.observe(this.pageHeader);
        this.headerNavigation.forEach((nav) => this.galleryResizeObserver.observe(nav));
      }
      this.updateStickyOffset();
    }
    document.addEventListener("visibilitychange", this.pageVisibilityHandler);
    window.addEventListener("pagehide", this.pauseMediaHandler);
    if ("IntersectionObserver" in window) {
      this.mediaVisibilityObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => !entry.isIntersecting)) this.pauseMedia();
      });
      this.mediaVisibilityObserver.observe(this.mainSlider);
    }
  }

  disconnectedCallback() {
    this.pauseMedia();
    document.removeEventListener("visibilitychange", this.pageVisibilityHandler);
    window.removeEventListener("pagehide", this.pauseMediaHandler);
    this.mediaVisibilityObserver?.disconnect();
    window.removeEventListener("resize", this.gallerySizeHandler);
    this.galleryResizeObserver?.disconnect();
    this.photosColumn?.style.removeProperty("--product-gallery-sticky-top");
    if (this.headerClearanceApplied) {
      document.body.style.removeProperty("--product-header-clearance");
    }
  }

  updateHeaderClearance() {
    if (
      window.innerWidth < 1024 ||
      !this.pageHeader ||
      !document.body.classList.contains("has-frosted-wrapper-padding")
    ) {
      if (this.headerClearanceApplied) {
        document.body.style.removeProperty("--product-header-clearance");
        this.headerClearanceApplied = false;
      }
      return 100;
    }

    let height = this.pageHeader.getBoundingClientRect().height;
    let stickyTop = 100;
    this.headerNavigation.forEach((nav) => {
      const navHeight = nav.getBoundingClientRect().height;
      if (!navHeight) return;
      stickyTop = Math.max(stickyTop, navHeight + 20);
      if (getComputedStyle(nav).position !== "fixed") return;

      // The sticky menu leaves normal flow. Reconstruct its original space,
      // excluding the duplicated announcement shown only inside the sticky menu.
      const placeholder = nav.parentElement.classList.contains("sticky-wrapper")
        ? nav.parentElement.getBoundingClientRect().height
        : 0;
      const stickyAnnouncement = nav.querySelector(".bfcm-header");
      height += navHeight - placeholder - (stickyAnnouncement?.getBoundingClientRect().height || 0);
    });
    document.body.style.setProperty("--product-header-clearance", `${Math.max(140, Math.ceil(height))}px`);
    this.headerClearanceApplied = true;
    return stickyTop;
  }

  updateStickyOffset() {
    if (!this.photosColumn) return;
    const stickyTop = this.updateHeaderClearance();
    if (window.innerWidth < 1024) {
      this.photosColumn.style.removeProperty("--product-gallery-sticky-top");
      return;
    }
    // Keep short galleries below the header; let tall ones scroll until their
    // thumbnails reach the bottom of the window, with 20px of breathing room.
    const offset = Math.min(
      stickyTop,
      window.innerHeight - this.photosColumn.getBoundingClientRect().height - 20
    );
    this.photosColumn.style.setProperty("--product-gallery-sticky-top", `${offset}px`);
  }

  pauseMedia() {
    this.mainSlider.querySelectorAll("video").forEach((video) => video.pause());
    this.mainSlider.querySelectorAll("iframe").forEach((iframe) => {
      let playerUrl;
      try {
        playerUrl = new URL(iframe.src, document.baseURI);
      } catch (error) {
        return;
      }
      const host = playerUrl.hostname;
      let message;
      if (/^(www\.)?youtube(-nocookie)?\.com$/.test(host)) {
        message = { event: "command", func: "pauseVideo", args: [] };
      } else if (host === "player.vimeo.com") {
        message = { method: "pause" };
      }
      if (message) {
        iframe.contentWindow?.postMessage(JSON.stringify(message), playerUrl.origin);
      }
    });
  }

  init() {
    const $thumbSlider = $(this.thumbSlider);
    const $mainSlider = $(this.mainSlider);
    $mainSlider
      .off("beforeChange.productGalleryMedia")
      .on("beforeChange.productGalleryMedia", this.pauseMediaHandler);

    // Slick duplicates boundary slides for looping. Keep those copies out of
    // the image lightbox, and route clicks on a copy to its original image.
    $mainSlider
      .off(".productGalleryLoop")
      .on("init.productGalleryLoop reInit.productGalleryLoop setPosition.productGalleryLoop", () => {
        $mainSlider.find(".slick-cloned [data-fancybox]").removeAttr("data-fancybox");
      })
      .on("click.productGalleryLoop", ".slick-cloned a[data-zoom]", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const slider = $mainSlider.slick("getSlick");
        const cloneIndex = Number($(event.currentTarget).closest(".slick-slide").attr("data-slick-index"));
        const originalIndex = ((cloneIndex % slider.slideCount) + slider.slideCount) % slider.slideCount;
        slider.$slides.eq(originalIndex).find("a[data-fancybox]").first().trigger("click");
      });

    $thumbSlider.slick({
      infinite: true,
      slidesToScroll: 1,
      asNavFor: $mainSlider,
      focusOnSelect: true,
      mobileFirst: true,

      slidesToShow: 4,
      arrows: false,
      swipeToSlide: true,
      swipe: true,
      draggable: true,
      touchMove: true,
      touchThreshold: 10,
      arrows: true,
      nextArrow:
        '<button type="button" class="slick-next"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 17 33" xml:space="preserve"><g id="e4eb89a6-f885-43b8-9259-0d6b1516fab0"><g id="_x38_e584754-6657-46f1-a9d8-2cfd6623b552"><g><polygon points="14.9,14.5 0,0 0,3.7 11.1,14.5 13.2,16.5 11.1,18.5 0,29.3 0,33 14.9,18.5 17,16.5 "/></g></g></g></svg></button>',
      prevArrow:
        '<button type="button" class="slick-prev"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 33"><g id="7f9a1925-e8c7-4614-8787-3c6095a9f6e1" data-name="Layer 2"><g id="c9b7920a-81fa-4bfe-ad13-4da717c6854b" data-name="Layer 1"><g id="c2d982ff-0cf6-4220-b365-47f30d708fea" data-name="e4eb89a6-f885-43b8-9259-0d6b1516fab0"><g id="f51d455e-6b9c-4c4e-96db-a5004582beda" data-name="8e584754-6657-46f1-a9d8-2cfd6623b552"><polygon points="0 16.5 2.1 18.5 17 33 17 29.3 5.9 18.5 3.8 16.5 5.9 14.5 17 3.7 17 0 2.1 14.5 0 16.5"/></g></g></g></g></svg></button>',
      // mobileFirst: true, so each `breakpoint` below is a MIN width and the
      // largest matching one wins.
      responsive: [
        {
          // Tablets, 750–1023px: unchanged from the original theme — vertical
          // strip down the left-hand side. Must stay, or tablets inherit the
          // desktop settings below.
          breakpoint: 750,
          settings: {
            slidesToShow: 4,
            vertical: true,
            swipeToSlide: false,
          },
        },
        {
          // Desktop, 1024px and up: horizontal strip beneath the hero, to
          // match the branchfurniture.ca product layout.
          //
          // 1023, not 1024. Slick's mobileFirst breakpoints fire on
          // `windowWidth > breakpoint` — strictly greater — whereas the CSS
          // uses `min-width: 1024px`, which fires AT 1024. Writing 1024 here
          // leaves exactly one pixel where the CSS lays the strip out
          // horizontally while slick still has it vertical, which blows the
          // thumbnail slider up to ~1880px tall. 1023 makes JS and CSS agree.
          breakpoint: 1023,
          settings: {
            slidesToShow: 5,
            vertical: false,
            swipeToSlide: true,
          },
        },
      ],
    });

    $mainSlider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: true,
      asNavFor: $thumbSlider,
      dots: false,
      speed: 800,
      arrows: true,
      prevArrow:
        '<button type="button" class="product-gallery__main-arrow product-gallery__main-arrow--prev" aria-label="Previous product image"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 6l-6 6 6 6" /></svg></button>',
      nextArrow:
        '<button type="button" class="product-gallery__main-arrow product-gallery__main-arrow--next" aria-label="Next product image"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6" /></svg></button>',
    });

    this.bindThumbnailScroll();

    $(window).on("resize", function () {
      // $mainSlider.slick('resize');
      // $thumbSlider.slick('resize');
    });
  }

  bindThumbnailScroll() {
    if (this.thumbnailWheelHandler) {
      this.thumbSlider.removeEventListener("wheel", this.thumbnailWheelHandler);
    }
    let accumulated = 0;
    let lastEvent = 0;
    let lastStep = -Infinity;
    this.thumbnailWheelHandler = (event) => {
      if (event.ctrlKey || !this.thumbSlider.classList.contains("slick-initialized")) return;
      const slider = $(this.thumbSlider).slick("getSlick");
      if (slider.options.vertical || slider.slideCount <= slider.options.slidesToShow) return;
      const horizontal = event.shiftKey && !event.deltaX ? event.deltaY : event.deltaX;
      if (!horizontal || (!event.shiftKey && Math.abs(horizontal) <= Math.abs(event.deltaY))) return;
      const delta = horizontal * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? slider.listWidth : 1);
      const direction = Math.sign(delta) * (slider.options.rtl ? -1 : 1);
      const lastSlide = slider.slideCount - slider.options.slidesToShow;
      if (!slider.options.infinite &&
          ((direction < 0 && slider.currentSlide <= 0) ||
           (direction > 0 && slider.currentSlide >= lastSlide))) {
        accumulated = 0;
        return;
      }
      event.preventDefault();
      const now = performance.now();
      if (now - lastEvent > 180 || Math.sign(accumulated) !== direction) accumulated = 0;
      lastEvent = now;
      accumulated += Math.abs(delta) * direction;
      if (Math.abs(accumulated) < 24 || now - lastStep < 180 || slider.animating) return;
      accumulated = 0;
      lastStep = now;
      $(this.thumbSlider).slick(direction > 0 ? "slickNext" : "slickPrev");
    };
    this.thumbSlider.addEventListener("wheel", this.thumbnailWheelHandler, { passive: false });
  }

  destroy() {
    this.pauseMedia();
    $(this.mainSlider).slick("unslick");
    $(this.thumbSlider).slick("unslick");
  }

  handleVariantChange(event) {
    const { variant } = event.detail;
    if (!variant) return;

    this.pauseMedia();
    const isSliderUpdated = this.buildVariantSlider(variant);
  }

  buildVariantSlider(variant) {
    // console.log(
    //   "Main Slider =>",
    //   this.querySelector(".product-gallery__slider")
    // );
    // console.log(
    //   "Thumb Slider =>",
    //   this.querySelector(".product-gallery__thumb__slider")
    // );

    // Bug 1 fix: no early return for single-image or missing variants
    const variantImages = this.variantsImages[variant.id] || [];

    // Combine variant photos with shared media in the product's media order.
    const allImages = [...variantImages.filter(img => img), ...this.genericMedia];

    if (this.strictVariantGallery) {
      const empty = allImages.length === 0;
      this.querySelector('.number-of-slides').style.display = empty ? 'none' : '';
      this.querySelector('.product-gallery__thumb').style.display = empty ? 'none' : '';
      if (empty) {
        this.destroy();
        const message = document.createElement('div');
        message.className = 'mg-gallery-empty';
        message.setAttribute('role', 'status');
        message.style.cssText = 'min-height:320px;display:flex;align-items:center;justify-content:center;text-align:center;padding:30px';
        message.textContent = this.querySelector('[data-gallery-empty]').content.textContent;
        this.mainSlider.replaceChildren(message);
        this.thumbSlider.replaceChildren(document.createElement('div'));
        this.querySelector('.toltal-number-of-slides').textContent = '0';
        this.init();
        return true;
      }
    }
    if (allImages.length === 0) return false;

    let variantImagesMarkup = "";
    let thumbVariantImagesMarkup = "";

    allImages.forEach((image, index) => {
      const mediaMarkup = this.nonImageMarkup[image.id];
      if (mediaMarkup) {
        variantImagesMarkup += mediaMarkup.main;
        thumbVariantImagesMarkup += mediaMarkup.thumb;
        return;
      }
      if(image) {
        // Reserve the image's natural proportions before its pixels load.
        const width = Number(image.width || image.preview_image?.width);
        const height = Number(image.height || image.preview_image?.height);
        const hasDimensions = Number.isFinite(width) && width > 0 &&
          Number.isFinite(height) && height > 0;
        const dimensions = hasDimensions ? `width="${width}" height="${height}"` : "";
        const aspectRatio = hasDimensions ? `data-aspectratio="${width / height}"` : "";
        // main slider
        const variantImageMarkup = `
        <div class="thumb ata thum-image filter-${image.id}" id="${image.id}">
          <a data-zoom class="fancybox" rel="gallery1" href="${image.src}" data-fancybox="images">
            <img id="product-featured-image-${image.id}" src="${image.src}" alt="${image.alt}" ${dimensions}
              class="lazyload" data-src="${image.src}"
            data-widths="[180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048]"
              ${aspectRatio} data-sizes="auto">
          </a>
        </div>
        `;

        variantImagesMarkup += variantImageMarkup;

        // thumb slider
        const thumbVariantImageMarkup = `
            <div class="item filter-${image.id}" id="${image.id}">
              <a href="javascript:void(0)" data-image="${image.src}" data-zoom-image="${image.src}">
                <img src="${image.src}" alt="${image.alt}" ${dimensions}>
              </a>
            </div>
            `;
        thumbVariantImagesMarkup += thumbVariantImageMarkup;
      }
    });

    if (variantImagesMarkup.length == 0) return false;
    this.querySelector(".toltal-number-of-slides").textContent =
      allImages.length;
    this.querySelector(".current-slide-number").textContent = 1;

    // variantImagesMarkup
    if (variantImagesMarkup.length > 0) {
      $(this.mainSlider).slick("unslick");
      this.querySelector(".product-gallery__slider").innerHTML = "";
      //
    }
    if (thumbVariantImagesMarkup.length > 0) {
      $(this.thumbSlider).slick("unslick");
      this.querySelector(".product-gallery__thumb__slider").innerHTML = "";
    }

    if (allImages.length > 0) {
      this.querySelector(".product-gallery__slider").innerHTML =
        variantImagesMarkup;
      this.querySelector(".product-gallery__thumb__slider").innerHTML =
        thumbVariantImagesMarkup;
      // Finish the rebuild before the browser paints the unstyled slides.
      this.init();
    }
    return true;
  }

  buildVariantSlider2(variant) {
    const variantImagesJsonString = document.querySelector(
      `#variant-images-${variant.id}`
    );
    if (!variantImagesJsonString) return;

    const variantImages = JSON.parse(variantImagesJsonString.innerHTML);
    let variantImagesMarkup = "";
    let thumbVariantImagesMarkup = "";

    variantImages.forEach(function (image, index) {
      // main slider
      const variantImageMarkup = `
        <div class="thumb ata thum-image filter-${image.id}" id="${image.id}">
          <a data-zoom class="fancybox" rel="gallery1" href="${image.cloudinary_src}" data-fancybox="images">
            <img id="product-featured-image-${image.id}" src="${image.cloudinary_src}" alt="${image.alt}"
              class="lazyload" data-src="${image.cloudinary_src}"
             data-widths="[180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048]"
              data-aspectratio="${image.cloudinary_src}" data-sizes="auto">
          </a>
        </div>
        `;

      variantImagesMarkup += variantImageMarkup;

      // thumb slider
      const thumbVariantImageMarkup = `
          <div class="item filter-${image.id}" id="${image.id}">
            <a href="javascript:void(0)" data-image="${image.cloudinary_src}" data-zoom-image="${image.cloudinary_src}">
              <img src="${image.cloudinary_src}" alt="${image.alt}">
            </a>
          </div>
          `;
      thumbVariantImagesMarkup += thumbVariantImageMarkup;
    });

    if (variantImagesMarkup.length == 0) return false;
    this.querySelector(".toltal-number-of-slides").textContent =
      variantImages.length;
    this.querySelector(".current-slide-number").textContent = 1;

    // variantImagesMarkup
    if (variantImagesMarkup.length > 0) {
      $(this.mainSlider).slick("unslick");
      this.querySelector(".product-gallery__slider").innerHTML = "";
      //
    }
    if (thumbVariantImagesMarkup.length > 0) {
      $(this.thumbSlider).slick("unslick");
      this.querySelector(".product-gallery__thumb__slider").innerHTML = "";
    }

    if (variantImages.length > 0) {
      this.querySelector(".product-gallery__slider").innerHTML =
        variantImagesMarkup;
      this.querySelector(".product-gallery__thumb__slider").innerHTML =
        thumbVariantImagesMarkup;
      setTimeout(() => {
        this.init();
      }, 500);
    }
    return true;
  }
}

$(document).ready(function () {
  customElements.define("product-gallery", ProductGallery);
});

